import type { FC, ReactNode } from "react";
import { Plus } from "@untitledui/icons";
import { Button, EmptyState } from "@tricura/atlas-ui";

interface TableEmptyStateProps {
    icon: FC<{ className?: string }>;
    /** Replaces the `FeaturedIcon` entirely, for empty states whose art was
     *  exported from Figma with its own container and shadows. `icon` is
     *  ignored when this is set. */
    illustration?: ReactNode;
    title: string;
    description: string;
    addLabel?: string;
    /** Omit to render a quiet empty state with no CTA — useful for
     *  read-only tables fed by upstream calculators. */
    onAdd?: () => void;
    /** Custom attribute for E2E testing — applied to the "Add Row" CTA. */
    "data-autotest"?: string;
}

export function TableEmptyState({
    icon,
    illustration,
    title,
    description,
    addLabel = "Add Row",
    onAdd,
    "data-autotest": dataAutotest,
}: TableEmptyStateProps) {
    return (
        <EmptyState size="md" className="px-6 pt-20 pb-16">
            <EmptyState.Header pattern="none">
                {illustration ?? <EmptyState.FeaturedIcon icon={icon} color="brand" theme="modern" size="lg" />}
            </EmptyState.Header>
            <EmptyState.Content>
                <EmptyState.Title>{title}</EmptyState.Title>
                <EmptyState.Description>{description}</EmptyState.Description>
            </EmptyState.Content>
            {onAdd && (
                <EmptyState.Footer>
                    <Button color="secondary" size="md" iconLeading={Plus} onClick={onAdd} data-autotest={dataAutotest}>
                        {addLabel}
                    </Button>
                </EmptyState.Footer>
            )}
        </EmptyState>
    );
}
